import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { rememberReturnTo } from '../lib/returnTo';
import { Button } from './ui/button';
import { Phone, Copy, Check, MessageCircle, FileCheck, FileText } from 'lucide-react';

/**
 * Reveals a Pro's contact (spec §5.1/§7.2). Logged-out users are sent to sign-up, with the
 * destination stored via rememberReturnTo — router state does NOT survive /auth's redirect to
 * /sign-up (it forwards only location.search) nor the full page reload every auth transition
 * performs, so a state-only returnTo silently dropped the visitor on /dashboard.
 * Logged-in users POST /api/pro-listings/{id}/contact — the ONE endpoint
 * that returns a phone — then see a copy button + tel: link + a Message button that navigates to
 * the returned message_deeplink. Re-clicking is idempotent (server no-ops, we just reshow).
 *
 * @param {string} listingId
 * @param {string} slug - for the login returnTo path.
 * @param {object|null} profile - the current user; null/undefined means logged out.
 */
const ShowContactButton = ({ listingId, slug, profile }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [contact, setContact] = useState(null); // { phone, message_deeplink, credentials: [{doc_type, doc_url, is_pdf}] }
  const [copied, setCopied] = useState(false);

  const returnTo = `/services/listings/${slug}`;

  const reveal = async () => {
    if (loading) return;

    // Logged out → auth, returning to this listing afterwards (matches JobDetails pattern).
    if (!profile?.id) {
      rememberReturnTo(returnTo);
      navigate('/auth');
      return;
    }

    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        rememberReturnTo(returnTo);
        navigate('/auth');
        return;
      }
      const res = await api.post(`/api/pro-listings/${listingId}/contact`, {}, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      setContact(res.data || {});
    } catch (err) {
      const status = err?.response?.status;
      const detail = typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : '';
      if (status === 401) {
        rememberReturnTo(returnTo);
        navigate('/auth');
        return;
      }
      if (status === 400) {
        toast.error(detail || t('services.contactSelfError'));
      } else if (status === 410) {
        toast.error(detail || t('services.contactUnavailableError'));
      } else {
        toast.error(detail || t('services.contactGenericError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const copyPhone = async () => {
    if (!contact?.phone) return;
    try {
      await navigator.clipboard.writeText(contact.phone);
      setCopied(true);
      toast.success(t('services.phoneCopied'));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t('services.copyFailed'));
    }
  };

  const openMessage = () => {
    if (contact?.message_deeplink) {
      navigate(contact.message_deeplink);
    }
  };

  // Revealed state — phone + copy + tel: link + Message button.
  // NOTE: the aria-live region is the PERSISTENT outer wrapper (rendered in both states below)
  // — live regions must exist in the DOM before content arrives to be reliably announced.
  const revealedBlock = contact && (
      <div className="space-y-3">
        {contact.phone ? (
          <div className="flex items-center justify-between gap-2 rounded-lg border border-border bg-muted/40 p-3">
            <a href={`tel:${contact.phone}`} className="flex items-center gap-2 font-semibold text-primary">
              <Phone size={16} aria-hidden="true" />
              {contact.phone}
            </a>
            <Button size="sm" variant="outline" className="min-h-[40px] px-4" onClick={copyPhone}>
              {copied ? <Check size={14} className="mr-1" aria-hidden="true" /> : <Copy size={14} className="mr-1" aria-hidden="true" />}
              {copied ? t('services.copied') : t('services.copy')}
            </Button>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t('services.noPhoneOnFile')}</p>
        )}
        {contact.message_deeplink && (
          <Button className="w-full min-h-[44px]" onClick={openMessage}>
            <MessageCircle size={16} className="mr-2" aria-hidden="true" />
            {t('services.messagePro')}
          </Button>
        )}
        {Array.isArray(contact.credentials) && contact.credentials.length > 0 && (
          <div className="space-y-2 rounded-lg border border-border bg-muted/40 p-3">
            <p className="text-sm font-medium flex items-center gap-1.5">
              <FileCheck size={14} className="text-emerald-600" aria-hidden="true" />
              {t('services.credentialsHeading')}
            </p>
            <ul className="space-y-1.5">
              {contact.credentials.map((c, i) => (
                <li key={`${c.doc_type}-${i}`}>
                  <a
                    href={c.doc_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <FileText size={14} aria-hidden="true" />
                    <span>{t(`services.verifDocType_${c.doc_type}`, c.doc_type)}</span>
                    <span className="text-xs text-muted-foreground">
                      ({c.is_pdf ? 'PDF' : t('services.credentialImage')})
                    </span>
                  </a>
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground">{t('services.credentialsSelfProvidedHint')}</p>
          </div>
        )}
        <p className="text-xs text-muted-foreground">{t('services.revealFollowupHint')}</p>
      </div>
  );

  return (
    <div aria-live="polite">
      {revealedBlock || (
        <div className="space-y-1">
          <Button className="w-full min-h-[44px]" size="lg" onClick={reveal} disabled={loading}>
            <Phone size={16} className="mr-2" aria-hidden="true" />
            {loading ? t('services.revealing') : t('services.showContact')}
          </Button>
          <p className="text-xs text-muted-foreground text-center">{t('services.showContactHint')}</p>
        </div>
      )}
    </div>
  );
};

export default ShowContactButton;
